import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { API } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  MilestoneBanner, NudgeCards, WeeklyBriefing, UpgradePrompt, useEngagement,
} from '../components/engagement/EngagementWidgets';
import {
  ChangesSection, ConnectionsSection, DoctorPrepCard, MemoryContext, usePersonalIntelligence,
} from '../components/intelligence/IntelligenceWidgets';
import { useHealth } from '../context/HealthContext';
import { motion } from 'framer-motion';
import {
  ChatCircle, Sun, UserCircle, CalendarBlank, Clock,
  ArrowUp, ArrowDown, Warning, Smiley, Bed, Lightning,
  CaretRight, Heartbeat, NotePencil, Brain, ShieldWarning,
  Pill, TrendUp, CalendarCheck, CheckCircle, ArrowRight, X as XIcon
} from '@phosphor-icons/react';
import { MainLayout } from '../components/shared/MainLayout';
import { DemoBadge } from '../components/shared/DemoBadge';
import { ActivationChecklist } from '../components/engagement/ActivationChecklist';
import { AppointmentPrepCard } from '../components/home/AppointmentPrepCard';
import { TodayPriorityCard } from '../components/home/TodayPriorityCard';
import { WinsProgressCard } from '../components/home/WinsProgressCard';
import { AskKindraStrip } from '../components/home/AskKindraStrip';
import WeeklyRecapCard from '../components/home/WeeklyRecapCard';
// Sprint C — Make Kindra Feel Alive
import { TodayFocusHero } from '../components/home/TodayFocusHero';
import FirstUploadNudge from '../components/home/FirstUploadNudge';
import TodaysOneThing from '../components/home/TodaysOneThing';
import QuickActions from '../components/home/QuickActions';
import ContinueWhereYouLeftOff from '../components/home/ContinueWhereYouLeftOff';
import WeeklyHealthBriefing from '../components/home/WeeklyHealthBriefing';
import { WhatChangedCard } from '../components/home/WhatChangedCard';
import { AskKindraNowCard } from '../components/home/AskKindraNowCard';
import { QuietWinsCard } from '../components/home/QuietWinsCard';
// Stage 5 — premium "Your Latest Insight" card
import { LatestInsightCard } from '../components/home/LatestInsightCard';
// Stage 10 — visible accumulated value: saved one-pagers card.
import { SavedOnePagersCard } from '../components/home/SavedOnePagersCard';
// Stage 11 — trial countdown banner with accumulated-value strip.
import TrialCountdownBanner from '../components/billing/TrialCountdownBanner';
import { trackOnce, ACTIVATION_EVENTS } from '../services/activation';
import { toast } from 'sonner';

const SIGNAL_ICONS = {
  trend_up: ArrowUp,
  trend_down: ArrowDown,
  alert: Warning,
  support: Smiley,
  symptoms: Heartbeat,
  event: NotePencil,
  calendar: CalendarBlank,
  streak: Lightning,
};

const SEVERITY_STYLES = {
  warning: 'bg-amber-50 border-amber-200 text-amber-900',
  positive: 'bg-emerald-50 border-emerald-200 text-emerald-900',
  info: 'bg-slate-50 border-slate-200 text-slate-700',
};

const SEVERITY_ICON_STYLES = {
  warning: 'text-amber-600 bg-amber-100',
  positive: 'text-emerald-600 bg-emerald-100',
  info: 'text-slate-500 bg-slate-100',
};

// Stage 9 polish — tiny "Your week so far" 2-stat row above the More toggle.
// Calm, never empty (renders zeroes confidently), reinforces daily rhythm.
const WeeklyAtAGlanceRow = ({ stats }) => {
  const checkins = stats?.checkins_this_week ?? 0;
  const docs = stats?.documents_count ?? 0;
  return (
    <section data-testid="home-week-row" className="grid grid-cols-2 gap-3">
      <div className="bg-white border border-[#ECE8E1] rounded-2xl px-4 py-3.5 flex items-baseline gap-3">
        <span
          className="font-serif text-[26px] tabular-nums text-[#0E1C2E] leading-none"
          data-testid="home-week-checkins"
        >
          {checkins}
        </span>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">This week</p>
          <p className="text-[12.5px] text-slate-600 leading-tight mt-0.5">check-in{checkins === 1 ? '' : 's'}</p>
        </div>
      </div>
      <div className="bg-white border border-[#ECE8E1] rounded-2xl px-4 py-3.5 flex items-baseline gap-3">
        <span
          className="font-serif text-[26px] tabular-nums text-[#0E1C2E] leading-none"
          data-testid="home-week-docs"
        >
          {docs}
        </span>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">On file</p>
          <p className="text-[12.5px] text-slate-600 leading-tight mt-0.5">document{docs === 1 ? '' : 's'}</p>
        </div>
      </div>
    </section>
  );
};

import { useSWRCache, peekSWRCache } from '../services/useSWRCache';

const HomePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Stage-15 P0 — cache-first hydration. /home/briefing is the largest payload
  // on Home and the single most expensive on cold mount. With SWR, every
  // return-from-Chat / return-from-Reports / return-from-Settings renders
  // the previous briefing instantly while a background fetch revalidates.
  const { data: briefing, refetch: refetchBriefing } = useSWRCache('/home/briefing', { ttlMs: 30_000 });
  const loading = briefing == null && !peekSWRCache('/home/briefing');
  // Local optimistic-dismiss state — keys that should immediately be
  // hidden from the home notification surfaces without a refetch.
  const [localDismissed, setLocalDismissed] = useState(new Set());
  // Stage 1-D: showMore is auto-expanded for engaged users (≥1 win OR
  // ≥2 what-changed signals) and starts collapsed for fresh users so
  // the above-fold stays a single calm focal point.
  const [showMore, setShowMore] = useState(false);
  const [showMoreAutoSet, setShowMoreAutoSet] = useState(false);
  const { healthVersion } = useHealth();
  const { data: engagement, ackMilestone } = useEngagement();
  const { data: intelligence } = usePersonalIntelligence();

  // Compatibility shim — preserves the existing call sites that expect
  // `fetchBriefing()` to force-refresh.
  const fetchBriefing = refetchBriefing;

  // Activation funnel: fire once per user when Home first renders.
  useEffect(() => {
    if (user && (user.id || user._id)) {
      trackOnce(ACTIVATION_EVENTS.FIRST_HOME_VIEWED);
    }
  }, [user?.id, user?._id]);

  // Re-fetch when health state changes (event-driven)
  useEffect(() => {
    if (healthVersion > 0) {
      fetchBriefing();
    }
  }, [healthVersion, fetchBriefing]);

  // Stage 2-P5 — engagement-based trial auto-extension. Fires once per
  // mount (ref-guarded) and surfaces a calm toast if the user just earned
  // an extra 7 days. No nags, no countdown timers — only a positive moment.
  const trialExtCheckedRef = useRef(false);
  useEffect(() => {
    if (trialExtCheckedRef.current) return;
    trialExtCheckedRef.current = true;
    (async () => {
      try {
        const { data } = await axios.post(`${API}/billing/check-extension`);
        if (data?.extended) {
          toast.success(
            `You're using Kindra well — we've added another ${data.bonus_days || 7} days free.`,
            { duration: 7000, id: 'trial-extension' }
          );
        }
      } catch { /* silent */ }
    })();
  }, []);

  // Stage 1-D: auto-expand "More from Kindra" once we know the user is
  // engaged (any wins, or 2+ what-changed signals). Only fires once so
  // the user can still collapse it manually. MUST be declared before any
  // early returns to satisfy React Hooks rules.
  useEffect(() => {
    if (showMoreAutoSet || !briefing) return;
    const w = briefing?.wins || [];
    const c = briefing?.what_changed || [];
    if (w.length >= 1 || c.length >= 2) {
      setShowMore(true);
    }
    setShowMoreAutoSet(true);
  }, [briefing, showMoreAutoSet]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const formatDate = (dateStr) => {
    try {
      // Stage 20B P0 — handle BOTH "YYYY-MM-DD" date-only strings (legacy)
      // AND full ISO strings ("YYYY-MM-DDTHH:MM:SS") returned by the
      // appointments API. Previously we always appended "T00:00:00" which
      // produced "2026-05-01T09:30:00T00:00:00" → "Invalid Date" in the UI.
      const safe = (typeof dateStr === 'string' && dateStr.includes('T'))
        ? dateStr
        : `${dateStr}T00:00:00`;
      const d = new Date(safe);
      if (Number.isNaN(d.getTime())) return dateStr;
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      if (d.toDateString() === today.toDateString()) return 'Today';
      if (d.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
      return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
    } catch {
      return dateStr;
    }
  };

  // Resolve a click destination for a home-notification item based on its
  // type and tags. Keeps routing intent in one place.
  const resolveInsightDestination = useCallback((insight) => {
    const type = insight?.type || '';
    const tags = Array.isArray(insight?.tags) ? insight.tags.join(' ').toLowerCase() : '';
    if (type === 'preappointment' || type === 'doctor_prep') {
      const nextAppt = (briefing?.upcoming || [])[0];
      if (nextAppt?.id) return `/appointments/${nextAppt.id}`;
      return '/appointments';
    }
    if (type === 'medication_tradeoff' || type === 'medication_impact' || tags.includes('medication')) {
      return '/health?tab=medications';
    }
    if (type === 'trend') return '/health?tab=trends';
    if (type === 'risk' || type === 'care_coordination') return '/chat';
    // cross_condition, pattern, stability, or unknown → health overview
    return '/health';
  }, [briefing]);

  // Dismiss handler — optimistic local remove + API persist. Rolls back
  // if the server call fails so nothing disappears silently.
  const dismissNotification = useCallback(async (key) => {
    if (!key) return;
    setLocalDismissed((prev) => new Set([...prev, key]));
    try {
      await axios.post(`${API}/home/dismiss`, { key });
    } catch (err) {
      console.warn('dismiss failed — restoring card', err);
      setLocalDismissed((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }, []);

  if (loading) {
    return (
      <MainLayout>
        <div className="px-6 py-6">
          {/* Premium skeleton — appears within 300ms, paper-tinted, not jarring slate */}
          <div className="animate-pulse">
            <div className="h-7 bg-[#ECE8E1]/70 rounded w-44 mb-2" />
            <div className="h-4 bg-[#ECE8E1]/50 rounded w-28 mb-8" />
            <div className="h-32 bg-[#ECE8E1]/60 rounded-2xl mb-4" />
            <div className="h-24 bg-[#ECE8E1]/40 rounded-2xl mb-4" />
            <div className="h-20 bg-[#ECE8E1]/30 rounded-2xl" />
          </div>
        </div>
      </MainLayout>
    );
  }

  const today = briefing?.today;
  const topPriority = briefing?.top_priority;
  const wins = briefing?.wins || [];
  const whatChanged = briefing?.what_changed || [];
  const rawInsights = briefing?.insights || [];

  const rawProactiveInsights = briefing?.proactive_insights || [];
  const rawSignals = briefing?.signals || [];
  // Apply local optimistic dismissals so cards disappear instantly.
  const insights = rawInsights.filter((i) => !i.notification_key || !localDismissed.has(i.notification_key));
  const proactiveInsights = rawProactiveInsights.filter((i) => !i.notification_key || !localDismissed.has(i.notification_key));
  const signals = rawSignals.filter((s) => !s.notification_key || !localDismissed.has(s.notification_key));
  const upcoming = briefing?.upcoming || [];
  const stats = briefing?.stats || {};
  const isNewUser = (stats.days_tracking || 0) < 3 && insights.length === 0;

  // Stage-14 — derive inputs for the single Today's One Thing card.
  // recentDocument: a doc analysed within the last 24h.
  const recentDocument = (briefing?.recent_documents || briefing?.documents || []).find((d) => {
    const at = d.analysed_at || d.created_at;
    if (!at) return false;
    return (Date.now() - new Date(at).getTime()) < 24 * 3600 * 1000;
  }) || null;
  // nextAppointment: first upcoming appointment.
  const nextAppointment = upcoming[0] || null;
  // lastChat: handed in by /home/briefing or null.
  const lastChat = briefing?.last_chat || null;

  const INSIGHT_ICONS = {
    risk: ShieldWarning,
    cross_condition: Brain,
    medication_impact: Pill,
    trend: TrendUp,
    preappointment: CalendarCheck,
    stability: CheckCircle,
  };

  const INSIGHT_SEVERITY = {
    warning: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-900', icon: 'text-amber-600 bg-amber-100' },
    info: { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700', icon: 'text-slate-500 bg-slate-100' },
    positive: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-900', icon: 'text-emerald-600 bg-emerald-100' },
  };

  return (
    <MainLayout desktopWide>
      {/* Desktop premium pass — wider canvas (lg:1440px) with a 2-column
          shell at lg+. Center column holds the existing primary feed; the
          right rail surfaces ambient cards (insight, what-changed, accumulated
          assets) so 700px of desktop white space carries actual content. */}
      <div className="px-4 sm:px-6 lg:px-10 py-4 lg:py-10 lg:max-w-[1440px] lg:mx-auto">
        <div className="lg:grid lg:grid-cols-12 lg:gap-10 lg:items-start">
          {/* CENTER (mobile: full; desktop: 7 of 12) */}
          <div className="lg:col-span-7 space-y-6">

        {/* Stage 11 — Trial countdown banner (auto-hides when >7 days left or already paid) */}
        <TrialCountdownBanner />

        {/* Header — Stage 4: editorial typography (serif italic name, calm date row) */}
        <header className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h1 data-testid="home-greeting" className="text-[24px] sm:text-[28px] font-medium text-[#0E1C2E] tracking-tight leading-tight">
                {getGreeting()},{' '}
                <em
                  className="italic font-medium text-[#0E1C2E]"
                  style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
                >
                  {user?.name?.split(' ')[0] || 'there'}
                </em>
              </h1>
              <DemoBadge user={user} />
            </div>
            <p className="text-[12.5px] text-slate-400 mt-1">
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
              {stats.days_tracking > 0 && (
                <>
                  <span className="mx-1.5 text-slate-300">·</span>
                  <span>{stats.days_tracking} day{stats.days_tracking !== 1 ? 's' : ''} tracked</span>
                </>
              )}
            </p>
          </div>
          <button
            data-testid="profile-btn"
            onClick={() => navigate('/profile')}
            className="w-11 h-11 min-w-[44px] min-h-[44px] bg-white border border-[#ECE8E1] rounded-full flex items-center justify-center hover:border-[#CFC8BB] hover:bg-[#FBFAF6] transition-colors flex-shrink-0"
            aria-label="Profile"
          >
            <UserCircle size={22} className="text-[#0E1C2E]" weight="duotone" />
          </button>
        </header>

        {/* ── First-session activation checklist.
            Stage 17 — HIDE on Day 0 (no uploads AND no chats yet).
            Stage 20B — backend `/home/briefing.stats` exposes `documents_count`
            and `log_count` (NOT `documents`/`chats`). Field-name mismatch
            previously meant the gate was always falsy → checklist never rendered
            for users with data. Now correctly gated on real activity. */}
        {((stats?.documents_count ?? 0) + (stats?.log_count ?? 0)) > 0 && <ActivationChecklist />}

        {/* ZONE 1 subline. Sets context for the single card below. */}
        <p className="text-[14px] text-slate-500 leading-relaxed -mt-3" data-testid="home-subline">
          Here's what matters today.
        </p>

        {/* ZONE 2 — Today's One Thing. ONE primary card, picked by priority.
            Replaces the previous stack of TodayFocusHero / AppointmentPrepCard
            / FirstUploadNudge / LatestInsightCard / SavedOnePagersCard /
            AskKindraNowCard / WeeklyAtAGlanceRow. Each of those still exists
            and still works — they're now reachable via the "More from your
            record" expansion or via direct navigation. */}
        <TodaysOneThing
          stats={stats}
          briefing={briefing}
          recentDocument={recentDocument}
          nextAppointment={nextAppointment}
          lastChat={lastChat}
        />

        {/* Stage 15 P1 — single-card "Continue where you left off". Auto-hides
            when nothing is resumable. Picks the most recent of: last chat /
            doc still processing / upcoming appointment without prep. */}
        <ContinueWhereYouLeftOff briefing={briefing} />

        {/* ZONE 3 — three quick actions. Always visible, always the same. */}
        <QuickActions />

        {/* Stage 19 P5 — gentle "Make Kindra more personal" nudge.
            Shows when the profile is thin (zero conditions on file) so a
            brand new user discovers they can add more context to improve
            Kindra's answers. No progress bar, no 0/5 counter, no guilt —
            just a calm invitation. Hides itself once the user has at least
            one condition.
            Stage 20B — was checking `medications_count` which doesn't exist
            on the briefing endpoint; corrected to gate on conditions only.
            Stage 20B+ — defer until briefing has loaded so the card never
            flashes for users whose stats just haven't arrived yet. */}
        {briefing && (stats?.conditions_count ?? 0) === 0 && (
          <section data-testid="home-make-personal" className="rounded-2xl border border-[#235347]/20 bg-[#235347]/[0.04] p-5">
            <p className="text-[10px] tracking-[0.18em] uppercase text-[#235347] font-semibold mb-2">
              Make Kindra more personal
            </p>
            <p className="text-[14px] text-[#0E1C2E] leading-relaxed mb-3">
              Add one condition, medication, symptom, or note so Kindra can answer with more context — not just general health info.
            </p>
            <button
              onClick={() => navigate('/health')}
              className="inline-flex items-center gap-1.5 text-[13px] font-medium text-white bg-[#235347] hover:bg-[#1c4439] rounded-full px-4 py-2 transition-colors"
              data-testid="home-make-personal-cta"
            >
              Update health profile
              <ArrowRight size={13} />
            </button>
          </section>
        )}

        {/* Stage 15 P2 — Weekly Health Briefing card. Auto-hides on Sun→Sat
            for users without enough activity yet. Calm editorial framing. */}
        <WeeklyHealthBriefing />

        {/* ZONE 4 — What changed (max 3). Plain language only. */}
        <WhatChangedCard items={(whatChanged || []).slice(0, 3)} loading={!briefing} />

        {/* ── Quiet "More from your record" expansion. Everything else lives
            here so the calm above-fold stays calm. */}
        <div className="pt-2">
          <button
            onClick={() => setShowMore((v) => !v)}
            className="text-[13px] text-slate-500 hover:text-[#0E1C2E] transition-colors inline-flex items-center gap-1.5"
            data-testid="home-show-more-toggle"
          >
            {showMore ? 'Show less' : 'More from your record'}
            <span className="text-slate-400 text-[11px]">{showMore ? '▲' : '▼'}</span>
          </button>
        </div>

        {!showMore ? null : <>

        {/* The original "above-fold" stack now lives below the fold, gated
            behind the toggle so power-users can still reach everything. */}
        <FirstUploadNudge />
        <AppointmentPrepCard />
        <LatestInsightCard />
        <SavedOnePagersCard />
        <AskKindraNowCard />
        <WeeklyAtAGlanceRow stats={stats} />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6">
          <QuietWinsCard wins={wins} />
        </div>

        {/* Stage 9 polish: removed duplicate "Today / How are you feeling?"
            check-in card and the redundant "What Kindra does for you" 4-row
            new-user block. TodayFocusHero (above) covers the daily check-in,
            and the activation checklist covers feature discovery. Audit P1. */}

        {/* ── Return Visit: Proactive Insights ── */}
        {!isNewUser && proactiveInsights.length > 0 && (
          <section data-testid="home-proactive">
            <div className="flex items-center gap-2 mb-3">
              <Lightning size={14} weight="duotone" className="text-amber-500" />
              <h2 className="text-xs font-medium uppercase tracking-wider text-slate-400">Since your last visit</h2>
            </div>
            <div className="space-y-2">
              {proactiveInsights.map((pi, idx) => {
                const nKey = pi.notification_key;
                return (
                  <motion.div
                    key={nKey || idx}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.06 }}
                    onClick={() => navigate('/chat')}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/chat'); } }}
                    className="bg-amber-50/60 border border-amber-200/50 rounded-xl p-4 cursor-pointer hover:shadow-sm active:scale-[0.995] transition-all relative group"
                    data-testid={`proactive-${idx}`}
                  >
                    <p className="text-sm text-amber-900 leading-relaxed pr-7">{pi.message}</p>
                    {pi.frequency && (
                      <p className="text-[10px] text-amber-600/60 mt-1.5">Based on {pi.frequency} mentions in your conversations</p>
                    )}
                    {nKey && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); dismissNotification(nKey); }}
                        aria-label="Dismiss"
                        data-testid={`proactive-dismiss-${idx}`}
                        className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center text-amber-700/60 hover:text-amber-900 hover:bg-white/60 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:focus:opacity-100 transition-opacity touch-manipulation"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <XIcon size={14} weight="bold" />
                      </button>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </section>
        )}

        {/* ── Insights (powered by health_state) ── */}
        {insights.length > 0 && (
          <section data-testid="home-insights">
            <div className="flex items-center gap-2 mb-3">
              <Brain size={14} weight="duotone" className="text-slate-400" />
              <h2 className="text-xs font-medium uppercase tracking-wider text-slate-400">Insights</h2>
            </div>
            <div className="space-y-2.5">
              {insights.map((insight, idx) => {
                const IconComp = INSIGHT_ICONS[insight.type] || Brain;
                const sev = INSIGHT_SEVERITY[insight.severity] || INSIGHT_SEVERITY.info;
                const destination = resolveInsightDestination(insight);
                const nKey = insight.notification_key;
                return (
                  <motion.div
                    key={nKey || idx}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6, transition: { duration: 0.18 } }}
                    transition={{ delay: idx * 0.06 }}
                    onClick={() => navigate(destination)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(destination);
                      }
                    }}
                    className={`${sev.bg} border ${sev.border} rounded-xl p-4 flex items-start gap-3 cursor-pointer hover:shadow-sm active:scale-[0.995] transition-all relative group`}
                    data-testid={`insight-${idx}`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${sev.icon}`}>
                      <IconComp size={16} weight="duotone" />
                    </div>
                    <div className="flex-1 min-w-0 pr-7">
                      <p className={`text-sm leading-relaxed ${sev.text}`}>{insight.text}</p>
                      <p className="text-[10px] text-slate-400 mt-1.5">
                        {insight.type === 'cross_condition' ? 'Based on your health record' :
                         insight.type === 'medication_impact' || insight.type === 'medication_tradeoff' ? 'Based on your medications' :
                         insight.type === 'trend' ? 'Based on recent check-ins' :
                         insight.type === 'preappointment' || insight.type === 'doctor_prep' ? 'Based on upcoming appointments' :
                         'Based on your data'}
                      </p>
                    </div>
                    <CaretRight size={14} className="text-slate-400 mt-1 flex-shrink-0 opacity-60" />
                    {nKey && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); dismissNotification(nKey); }}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); } }}
                        aria-label="Dismiss insight"
                        data-testid={`insight-dismiss-${idx}`}
                        className="absolute top-0 right-0 w-11 h-11 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-white/60 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:focus:opacity-100 transition-opacity touch-manipulation"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <XIcon size={14} weight="bold" />
                      </button>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </section>
        )}

        {/* ── Signals ── */}
        {signals.length > 0 && (
          <section data-testid="home-signals">
            <h2 className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-3">Signals</h2>
            <div className="space-y-2.5">
              {signals.map((signal, idx) => {
                const IconComp = SIGNAL_ICONS[signal.icon] || Warning;
                const style = SEVERITY_STYLES[signal.severity] || SEVERITY_STYLES.info;
                const iconStyle = SEVERITY_ICON_STYLES[signal.severity] || SEVERITY_ICON_STYLES.info;
                const destination = signal.action?.path || (upcoming[0]?.id ? `/appointments/${upcoming[0].id}` : '/health');
                const nKey = signal.notification_key;
                return (
                  <motion.div
                    key={nKey || idx}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    onClick={() => navigate(destination)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(destination); } }}
                    className={`border rounded-xl p-4 flex items-start gap-3 cursor-pointer hover:shadow-sm active:scale-[0.995] transition-all relative group ${style}`}
                    data-testid={`signal-${idx}`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${iconStyle}`}>
                      <IconComp size={16} weight="bold" />
                    </div>
                    <div className="flex-1 min-w-0 pr-7">
                      <p className="text-sm leading-relaxed">{signal.message}</p>
                      {signal.context && (
                        <p className="text-xs leading-relaxed mt-1 opacity-70">{signal.context}</p>
                      )}
                    </div>
                    <CaretRight size={16} className="text-slate-400 mt-1 flex-shrink-0" />
                    {nKey && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); dismissNotification(nKey); }}
                        aria-label="Dismiss signal"
                        data-testid={`signal-dismiss-${idx}`}
                        className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-white/60 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:focus:opacity-100 transition-opacity touch-manipulation"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <XIcon size={14} weight="bold" />
                      </button>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </section>
        )}

        {/* ── Signals empty state (new user / insufficient data) ── */}
        {signals.length === 0 && stats.log_count < 4 && (
          <section data-testid="home-signals-empty">
            <h2 className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-3">Signals</h2>
            <div className="border border-dashed border-slate-200 rounded-xl p-5 text-center">
              <p className="text-sm text-slate-400">
                {stats.log_count === 0
                  ? 'Start logging daily to unlock health signals.'
                  : `${4 - stats.log_count} more check-in${4 - stats.log_count > 1 ? 's' : ''} to surface your first patterns.`}
              </p>
            </div>
          </section>
        )}

        {/* ── Upcoming ── */}
        {upcoming.length > 0 && (
          <section data-testid="home-upcoming">
            <h2 className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-3">Upcoming</h2>
            <div className="space-y-2">
              {upcoming.map((appt) => (
                <motion.button
                  key={appt.id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  onClick={() => navigate(`/appointments/${appt.id}`)}
                  className="w-full bg-white border border-slate-150 rounded-xl p-4 text-left hover:border-slate-300 transition-colors"
                  data-testid={`upcoming-${appt.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <h3 className="font-medium text-slate-900 text-sm truncate">{appt.title}</h3>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                          <CalendarBlank size={12} />
                          {formatDate(appt.date)}
                        </span>
                        {appt.time && (
                          <span className="text-xs text-slate-500 flex items-center gap-1">
                            <Clock size={12} />
                            {appt.time}
                          </span>
                        )}
                        {appt.clinician_name && (
                          <span className="text-xs text-slate-400">{appt.clinician_name}</span>
                        )}
                      </div>
                      {appt.linked_conditions?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {appt.linked_conditions.slice(0, 2).map((c, i) => (
                            <span key={i} className="text-[9px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">{c}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {appt.prep_status === 'ready' && (
                        <span className="text-[9px] font-semibold bg-emerald-50 text-emerald-600 border border-emerald-200 px-2 py-0.5 rounded-full" data-testid={`prep-ready-${appt.id}`}>
                          Report ready
                        </span>
                      )}
                      {appt.prep_status === 'generating' && (
                        <span className="text-[9px] text-blue-500 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-full">
                          Preparing...
                        </span>
                      )}
                      <CaretRight size={16} className="text-slate-300" />
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>
          </section>
        )}

        {/* ── Personal Intelligence ── */}
        {intelligence && (
          <>
            <ChangesSection changes={intelligence.changes} />
            <ConnectionsSection connections={intelligence.connections} />
            <DoctorPrepCard doctorPrep={intelligence.doctor_prep} />
            <MemoryContext notes={intelligence.memory_context} />
          </>
        )}

        {/* ── Quick Actions ── Stage 4: cleaner, premium tile treatment */}
        <section data-testid="home-actions">
          <h2 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 mb-3 px-0.5">Quick actions</h2>
          <div className="grid grid-cols-3 gap-2.5">
            <button
              data-testid="quick-chat"
              onClick={() => navigate('/chat')}
              className="bg-white border border-[#ECE8E1] rounded-2xl p-4 flex flex-col items-start gap-3 hover:border-[#CFC8BB] hover:bg-[#FBFAF6] transition-all min-h-[112px] text-left"
            >
              <div className="w-9 h-9 bg-[#0E1C2E] rounded-xl flex items-center justify-center">
                <ChatCircle size={18} className="text-white" weight="fill" />
              </div>
              <div>
                <p className="text-[13px] font-semibold text-[#0E1C2E] leading-tight">Ask Kindra</p>
                <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">Anything on your mind</p>
              </div>
            </button>
            <button
              data-testid="quick-upload"
              onClick={() => navigate('/documents')}
              className="bg-white border border-[#ECE8E1] rounded-2xl p-4 flex flex-col items-start gap-3 hover:border-[#CFC8BB] hover:bg-[#FBFAF6] transition-all min-h-[112px] text-left"
            >
              <div className="w-9 h-9 bg-[#2FB3A0] rounded-xl flex items-center justify-center">
                <NotePencil size={18} className="text-white" weight="fill" />
              </div>
              <div>
                <p className="text-[13px] font-semibold text-[#0E1C2E] leading-tight">Add a report</p>
                <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">Bloods · scans · letters</p>
              </div>
            </button>
            <button
              data-testid="quick-health"
              onClick={() => navigate('/health')}
              className="bg-white border border-[#ECE8E1] rounded-2xl p-4 flex flex-col items-start gap-3 hover:border-[#CFC8BB] hover:bg-[#FBFAF6] transition-all min-h-[112px] text-left"
            >
              <div className="w-9 h-9 bg-[#235347] rounded-xl flex items-center justify-center">
                <Heartbeat size={18} className="text-white" weight="fill" />
              </div>
              <div>
                <p className="text-[13px] font-semibold text-[#0E1C2E] leading-tight">Health record</p>
                <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">Conditions · meds · timeline</p>
              </div>
            </button>
          </div>
        </section>

        {/* ── Engagement: Milestone + Nudges + Briefing + Upgrade ── */}
        {engagement && (
          <>
            <MilestoneBanner
              milestone={engagement.milestones?.new}
              onDismiss={() => engagement.milestones?.new && ackMilestone(engagement.milestones.new.id)}
            />
            {engagement.nudges?.length > 0 && (
              <section data-testid="home-nudges">
                <h2 className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-3">Waiting for you</h2>
                <NudgeCards nudges={engagement.nudges} />
              </section>
            )}
            <WeeklyBriefing briefing={engagement.briefing} />
            <UpgradePrompt upgrade={engagement.upgrade} plan={user?.plan_type} />
          </>
        )}

        </>}
        {/* END: collapsed secondary surfaces */}

          </div>{/* /CENTER */}

          {/* RIGHT RAIL (lg+ only) — ambient cards. On mobile these
              already live in the main flow above so the right rail is
              hidden to prevent duplication.
              Stage 17 — the "In your record" grid used to show 4 zeros
              on Day 0 ("0 reports analysed · 0 saved one-pagers · 0
              conversations remembered · 0 days tracked"). That reads as
              a report card, not a welcome. We now swap it for a warm
              empty-state card until the user has at least one doc or
              chat on file. */}
          <aside className="hidden lg:block lg:col-span-5 lg:sticky lg:top-10 space-y-6" data-testid="home-right-rail">
            {/* Stage 20B — gate by `documents_count` (the real field name on
                /home/briefing.stats). Was reading `stats.documents` which is
                always undefined → empty card always rendered for users with
                data.
                Stage 20B+ — (a) defer until briefing has loaded to kill the
                first-paint flicker for returning users with rich data; (b)
                split copy — true Day-0 (no docs, no conditions, no log) keeps
                the warm "starts empty" line; users who already have non-doc
                signal get a precise "one piece missing — your reports." */}
            {!briefing ? null : (stats?.documents_count ?? 0) === 0 ? (
              <div className="rounded-2xl border border-[rgba(14,28,46,0.10)] bg-white p-6" data-testid="home-empty-welcome">
                <p className="text-[10px] tracking-[0.18em] uppercase text-slate-400 font-medium mb-3">You're set up</p>
                <p
                  className="text-[22px] leading-[1.25] text-[#0E1C2E] mb-3"
                  style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
                >
                  {((stats?.conditions_count ?? 0) + (stats?.log_count ?? 0)) > 0
                    ? <>One piece still missing — <em>your reports.</em></>
                    : <>Your record starts empty — that's fine.</>}
                </p>
                <p className="text-[13.5px] text-slate-600 leading-relaxed">
                  {((stats?.conditions_count ?? 0) + (stats?.log_count ?? 0)) > 0
                    ? <>Drop in one report — a blood test, an imaging result, or a GP letter — and Kindra can ground every answer in your real labs, not just general health info.</>
                    : <>The fastest way to make Kindra useful is to drop in one report — a blood test, an imaging result, or a GP letter. Kindra reads it, explains it in plain English, and remembers it for every future answer.</>}
                </p>
                <p className="mt-4 text-[12px] text-slate-400 leading-relaxed">
                  No pressure — you can also just ask a question to try it.
                </p>
              </div>
            ) : (
            <div className="rounded-2xl border border-[rgba(14,28,46,0.10)] bg-white p-6" data-testid="home-record-summary">
              <p className="text-[10px] tracking-[0.18em] uppercase text-slate-400 font-medium mb-2">In your record</p>
              <div className="text-[13.5px] text-slate-600 leading-relaxed">
                {/* Stage 20B — replaced the 4 rows that referenced fields the
                    API never surfaces (`chats`, `one_pagers`) with the 4 fields
                    that DO exist on /home/briefing.stats. All counters now show
                    real numbers instead of permanent zeros. */}
                <span className="block mb-2">
                  <span
                    className="font-medium text-[#0E1C2E] mr-2"
                    style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontStyle: 'italic', fontSize: '20px' }}
                  >
                    {(stats?.documents_count ?? 0)}
                  </span>
                  reports analysed
                </span>
                <span className="block mb-2">
                  <span
                    className="font-medium text-[#0E1C2E] mr-2"
                    style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontStyle: 'italic', fontSize: '20px' }}
                  >
                    {(stats?.conditions_count ?? 0)}
                  </span>
                  conditions tracked
                </span>
                <span className="block mb-2">
                  <span
                    className="font-medium text-[#0E1C2E] mr-2"
                    style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontStyle: 'italic', fontSize: '20px' }}
                  >
                    {(stats?.log_count ?? 0)}
                  </span>
                  check-ins
                </span>
                <span className="block">
                  <span
                    className="font-medium text-[#0E1C2E] mr-2"
                    style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontStyle: 'italic', fontSize: '20px' }}
                  >
                    {(stats?.days_tracking ?? 0)}
                  </span>
                  days with Kindra
                </span>
              </div>
              <p className="mt-4 text-[11px] text-slate-400 leading-relaxed">
                Kindra reads, explains, and remembers — every conversation starts with what you already have.
              </p>
            </div>
            )}

            <div className="rounded-2xl border border-[rgba(14,28,46,0.10)] bg-white p-6">
              <p className="text-[10px] tracking-[0.18em] uppercase text-slate-400 font-medium mb-3">Quick actions</p>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => navigate('/chat')}
                  className="text-left text-[13.5px] text-[#0E1C2E] hover:bg-[#FBFAF6] rounded-lg px-3 py-2 transition-colors"
                  data-testid="rail-action-chat"
                >
                  Open a fresh chat →
                </button>
                <button
                  onClick={() => navigate('/documents')}
                  className="text-left text-[13.5px] text-[#0E1C2E] hover:bg-[#FBFAF6] rounded-lg px-3 py-2 transition-colors"
                  data-testid="rail-action-upload"
                >
                  Upload a report →
                </button>
                <button
                  onClick={() => navigate('/health')}
                  className="text-left text-[13.5px] text-[#0E1C2E] hover:bg-[#FBFAF6] rounded-lg px-3 py-2 transition-colors"
                  data-testid="rail-action-health"
                >
                  See my health record →
                </button>
                <button
                  onClick={() => navigate('/appointments')}
                  className="text-left text-[13.5px] text-[#0E1C2E] hover:bg-[#FBFAF6] rounded-lg px-3 py-2 transition-colors"
                  data-testid="rail-action-appts"
                >
                  Plan an appointment →
                </button>
              </div>
            </div>
          </aside>{/* /RIGHT RAIL */}

        </div>{/* /lg:grid */}
      </div>
    </MainLayout>
  );
};

export default HomePage;
